import { createNativePlatform } from "./native";
import type { Platform, PlatformKind } from "./types";
import { createWebPlatform } from "./web";

export type { AttachContext, Platform, PlatformKind } from "./types";

type Host = {
  __TAURI_INTERNALS__?: unknown;
  Capacitor?: { isNativePlatform?: () => boolean };
};

/**
 * Which shell the web app is running in. Tauri injects __TAURI_INTERNALS__ and Capacitor's native bridge
 * injects the Capacitor global, so the plain web bundle never loads either SDK.
 */
export function detectPlatform(host: Host = globalThis as Host): PlatformKind {
  if (host.__TAURI_INTERNALS__) return "tauri";
  if (host.Capacitor?.isNativePlatform?.()) return "capacitor";
  return "web";
}

let current: Platform | null = null;

/** The platform chosen at startup. Before loadPlatform runs, and in tests, this is the web platform. */
export function currentPlatform(): Platform {
  current ??= createWebPlatform();
  return current;
}

export function setPlatform(platform: Platform | null): void {
  current = platform;
}

/** Picks the platform for this run. The desktop and mobile code is only fetched when it is needed. */
export async function loadPlatform(kind: PlatformKind = detectPlatform()): Promise<Platform> {
  const buildServer = import.meta.env.VITE_RHEMA_SERVER as string | undefined;
  if (kind === "tauri") {
    const { tauriAdapters } = await import("./tauri");
    current = await createNativePlatform(tauriAdapters(), { buildServer });
  } else if (kind === "capacitor") {
    const { capacitorAdapters } = await import("./capacitor");
    current = await createNativePlatform(capacitorAdapters(), { buildServer });
  } else {
    current = createWebPlatform();
  }
  return current;
}
